<template>
  <div class="search-song">
    <div class="search-song__title">
      {{ title }}
    </div>
    <sib-input
      ref="searchInput"
      @blur="blurSearchBar"
      @focus="focusSearchBar"
      v-model="searchQuery"
      size="md"
      round="md"
      :placeholder="$props.placeholder"
      :is-show-clear-button="true"
    ></sib-input>
    <input
      v-if="required && !songSelected"
      type="text"
      required
      id="inputSongValidate"
    />

    <div
      class="song-selected"
      :class="{ 'song-selected--loading': loadingSelect }"
      v-if="songSelected"
    >
      <div class="search-item__detail">
        <div class="search-item__image-artist">
          <img
            :src="songSelected.image"
            alt="album image"
            class="search-item__image"
            @error="fallBackImage"
          />
          <div
            v-show="loadingSelect"
            class="search-item__image--loading lds-ring"
          >
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>

        <div class="search-item__info ml-sm">
          <div :title="songSelected.name" class="search-item__title">
            <span v-html="songSelected.name"></span>
          </div>
          <div :title="songSelected.artist" class="search-item__artist">
            <span v-html="songSelected.artist"></span>
          </div>
        </div>
      </div>
      <CloseIcon
        v-show="!loading"
        class="mr-sm close-icon"
        width="17"
        @click="removeSongSelected"
      />
    </div>

    <div
      ref="searchListElement"
      class="search-list"
      v-show="(showSearchList && searchQuery) || noResult"
      @scroll="scrollSearchList"
    >
      <div class="search--no-result" v-if="noResult">
        <p>{{ noResultText }}</p>
      </div>

      <template v-if="loading">
        <template v-for="n in 3" :key="n">
          <div class="search-item search-item-loading">
            <div class="search-item__detail">
              <sib-skeleton-loading
                width="60px"
                height="60px"
                round="sm"
              ></sib-skeleton-loading>
              <div class="search-item__info ml-sm">
                <div class="search-item__title">
                  <sib-skeleton-loading width="5rem"></sib-skeleton-loading>
                </div>
                <div class="search-item__artist">
                  <sib-skeleton-loading width="5rem"></sib-skeleton-loading>
                </div>
              </div>
            </div>
            <sib-skeleton-loading
              width="5rem"
              height="2rem"
              class="mr-sm"
            ></sib-skeleton-loading>
          </div>
        </template>
      </template>
      <template v-if="!loading">
        <template v-for="(item, i) in listSong" :key="i">
          <div class="search-item" @click="selectSong(item)">
            <div class="search-item__detail">
              <img
                :src="item.image"
                alt="album image"
                class="search-item__image"
                @error="fallBackImage"
              />
              <div class="search-item__info ml-sm">
                <div :title="item.name" class="search-item__title">
                  <span v-html="item.name"></span>
                </div>
                <div :title="item.artist" class="search-item__artist">
                  <span v-html="item.artist"></span>
                </div>
              </div>
            </div>
            <sib-button
              @click.prevent=""
              size="sm"
              width="auto"
              role="primary"
              class="mr-sm"
            >
              Apply
            </sib-button>
          </div>
        </template>
        <div class="search-list__search-loading" v-if="!noResult">
          <EllipsisLoading />
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, watch } from 'vue'
import { QUERY_STRING_NO_CACHE, debounce } from '@/utils'
import CloseIcon from '@/assets/images/icons/icon-close.svg'
import useCustomizationStore from '@/store/customization'
import EllipsisLoading from '@/ui-kits/EllipsisLoading.vue'
import { useRoute } from 'vue-router'
import { NullOrType } from '@/types'

const customizationStore = useCustomizationStore()
const route = useRoute()

interface Song {
  name: string
  artist: string
  image: string
  url: string
  uri: string
  nextPage: string
}

const props = withDefaults(
  defineProps<{
    appName?: string
    title?: string
    placeholder?: string
    loadingSelect?: boolean
    required?: boolean
  }>(),
  {
    appName: 'spotify',
    loadingSelect: false,
    required: false,
  }
)

const emit = defineEmits<{
  (e: 'change-spotify-song-selected', songSelected: Song): void
  (e: 'remove-spotify-song-selected'): void
}>()

const searchQuery = ref('')
const recentlySearchQuery = ref('')

const listSong = ref<Song[]>([])
const songSelected = ref<NullOrType<Song>>(null)
const searchListElement = ref<NullOrType<HTMLElement>>(null)
const loading = ref(false)
const showSearchList = ref(false)
const noResult = ref(false)
const noResultText = `We couldn't find song for your search!`
const SEARCH_LIST_HIDDEN_CLASS = 'search-list-hidden'

onMounted(() => {
  const searchDebounce = debounce(searchSong, 300)
  watch(
    () => searchQuery.value,
    (value) => {
      if (!value) {
        recentlySearchQuery.value = ''
        return
      }
      searchDebounce()
    }
  )

  // show message validate input song
  const inputSongValidate: HTMLInputElement | null =
    document.querySelector('#inputSongValidate')
  if (inputSongValidate) {
    inputSongValidate.addEventListener('invalid', function (e: Event) {
      inputSongValidate.setCustomValidity('Please choose your song')
    })
  }
})

async function scrollSearchList() {
  if (!searchListElement.value) return
  const { scrollTop, clientHeight, scrollHeight } = searchListElement.value
  const isLoadMore = scrollTop + clientHeight >= scrollHeight - 1

  if (isLoadMore && listSong.value.length) {
    const nextPage = listSong.value.at(-1)?.nextPage
    if (!nextPage) return
    const payload = {
      nextPage,
      isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
    }
    const tracks = await customizationStore.searchSong(payload)
    listSong.value = [...listSong.value, ...tracks]
  }
}

async function searchSong() {
  if (!searchQuery.value) return
  if (searchQuery.value === recentlySearchQuery.value) return
  recentlySearchQuery.value = searchQuery.value
  listSong.value = []
  loading.value = true
  showSearchList.value = true
  const payload = {
    songName: searchQuery.value,
    appName: props.appName,
    isForceLoadNewData: !!route.query[QUERY_STRING_NO_CACHE],
  }
  const tracks = await customizationStore.searchSong(payload)
  if (tracks?.length) {
    listSong.value = [...listSong.value, ...tracks]
    noResult.value = false
  } else {
    noResult.value = true
  }
  loading.value = false
}

function selectSong(song: Song) {
  if (song == songSelected.value) return
  songSelected.value = song
  showSearchList.value = false
  if (!songSelected.value?.uri) return
  emit('change-spotify-song-selected', songSelected.value)
}

function removeSongSelected() {
  songSelected.value = null
  emit('remove-spotify-song-selected')
}

function focusSearchBar() {
  searchListElement.value?.classList.remove(SEARCH_LIST_HIDDEN_CLASS)
  if (listSong.value.length && searchQuery.value) {
    showSearchList.value = true
    return
  }
  showSearchList.value = false
}

function blurSearchBar() {
  searchListElement.value?.classList.add(SEARCH_LIST_HIDDEN_CLASS)
  setTimeout(() => (showSearchList.value = false), 250)
}

function fallBackImage(event: any) {
  event.currentTarget.src =
    'https://cdn.shopify.com/s/files/1/0595/6042/7716/files/default-image.png'
}
</script>

<style lang="scss">
.search-song {
  position: relative;
  &__title {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  &.search-list-top-sm {
    .search-list {
      top: 52px;
    }
  }
  .search-list {
    --background-color: #e5e5ea;
    position: absolute;
    top: 80px;
    width: 100%;
    background: var(--background-color);
    z-index: var(--base-z-index);
    min-height: 6rem;
    border-radius: var(--border-radius-small);
    padding: 0.5rem;
    max-height: 27rem;
    overflow-y: auto;
    transition: transform 0.5s ease-in-out;
    opacity: 1;

    &__search-loading {
      .lds-ellipsis {
        --lds-ellipsis: 6px;
        --lds-ellipsis-top: 3px;
        height: 10px;
        left: 50%;
        transform: translateX(-50%);
      }
    }
    &-hidden {
      opacity: 0;
      transition: opacity 0.3s linear;
    }
    &::-webkit-scrollbar-track {
      background-color: var(--background-color);
    }
    &::-webkit-scrollbar {
      width: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #b4b4b4;
      border-radius: 1rem;
    }
  }
  .search--no-result {
    display: flex;
    justify-content: center;
  }
  .search-item {
    margin: 0.75rem 1rem;
    width: calc(100% - 2rem);
    cursor: pointer;
  }
  .song-selected {
    margin: 0.75rem 0;
    width: 100%;
    @include media-sm-down {
      .search-item__title {
        max-width: 220px;
      }
    }
    .close-icon {
      cursor: pointer;
      path {
        fill: var(--text-color-secondary);
      }
    }

    &--loading {
      opacity: 0.7;
    }
  }
  .search-item,
  .song-selected {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border-radius: var(--border-radius-small);
    &__detail {
      display: flex;
      align-items: center;
      width: calc(100% - 90px);
    }
    &__image-artist {
      position: relative;
      height: 60px;
      width: 60px;
    }
    &__image {
      min-width: 60px;
      width: 60px;
      height: 60px;
      border-radius: var(--border-radius-small) 0 0 var(--border-radius-small);
      overflow: hidden;
      object-fit: cover;

      &--loading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 1;
        font-size: 10px;
        --loading-size: 20px;
        --loading-line-width: 2px;
        color: #fff;
      }
    }
    &__info {
      display: flex;
      flex-direction: column;
      width: calc(100% - 75px);
      max-width: 200px;
    }

    &__title {
      font-weight: 700;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__artist {
      font-size: 13px;
      color: var(--text-color-secondary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  #inputSongValidate {
    position: absolute;
    bottom: 0;
    opacity: 0;
    z-index: -1;
  }
}
</style>
